import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2, ArrowLeft } from "lucide-react";
import staioIcon from "@/assets/images/staio-icon.png";
import { Redirect, Link } from "wouter";

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

export default function AdminLoginPage() {
  const { user, login, isLoggingIn, isLoading } = useAuth();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", password: "" },
  });

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  if (user) {
    const role = (user as any).role || 'super_admin';
    if (role === 'platform_admin') return <Redirect to="/platform" />;
    if (role === 'cleaner') return <Redirect to="/cleaner" />;
    if (role === 'maintenance') return <Redirect to="/maintenance" />;
    if (role === 'owner') return <Redirect to="/owner" />;
    if (role === 'affiliate') return <Redirect to="/affiliate" />;
    return <Redirect to="/admin" />;
  }

  async function onSubmit(values: z.infer<typeof loginSchema>) {
    try {
      await login(values);
    } catch (error) {
      form.setError("root", {
        message: error instanceof Error ? error.message : "Login failed"
      });
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(35 40% 95%) 50%, hsl(25 30% 94%) 100%)" }}>
      <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-amber-200/20 to-orange-200/10 rounded-full blur-3xl -z-10" />
      <Card className="w-full max-w-md border-t-4 border-t-transparent relative overflow-hidden" style={{ borderImage: "linear-gradient(to right, #f59e0b, #ea580c) 1" }}>
        <CardHeader className="text-center space-y-3">
          <img src={staioIcon} alt="Staio" className="w-14 h-14 mx-auto" />
          <div>
            <CardTitle className="text-2xl font-display font-bold" data-testid="text-login-title">Staio</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your account</p>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter username" {...field} data-testid="input-login-username" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="Enter password" {...field} data-testid="input-login-password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.formState.errors.root && (
                <div className="text-sm font-medium text-destructive text-center" data-testid="text-login-error">
                  {form.formState.errors.root.message}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={isLoggingIn} data-testid="button-login-submit">
                {isLoggingIn ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Sign In"}
              </Button>
            </form>
          </Form>
          <div className="mt-4 flex items-center justify-between">
            <Link href="/">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors" data-testid="link-back-home">
                <ArrowLeft className="w-3 h-3" />
                Back to home
              </span>
            </Link>
            <Link href="/register">
              <span className="text-xs text-primary hover:text-primary/80 cursor-pointer transition-colors" data-testid="link-create-account">
                Create an account
              </span>
            </Link>
          </div>
          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-xs text-muted-foreground" data-testid="text-contact-info">
              Need help? Contact us at{" "}
              <a href="mailto:info@staio.io" className="text-primary hover:underline">info@staio.io</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
